/**
 * Конфигурация каналов отправки сообщений
 * Дата: 28.12.2025 - Создан для TODO-016 (Messages UI Improvements)
 * Дата: 29.12.2025 - Обновлено: добавлены telegram_bot, telegram_client, max
 * Связан с: frontend/src/components/ui/ChannelSelector.tsx, frontend/src/pages/Messages.tsx
 * Компонент: Конфигурация каналов для расширяемой архитектуры
 */

export type ChannelId = 
  | 'whatsapp_waba' 
  | 'whatsapp_regular' 
  | 'telegram' 
  | 'telegram_bot' 
  | 'telegram_client' 
  | 'max'
  | 'max_client'
  | 'sms'
  | 'sms_tel';

export interface ChannelConfig {
  id: ChannelId;
  name: string;
  displayName?: string;
  icon: string;
  iconUrl?: string;
  color: string;
  requiresBinding?: boolean; // Требует привязки пациента
}

export const CHANNELS: Record<ChannelId, ChannelConfig> = {
  whatsapp_waba: {
    id: 'whatsapp_waba',
    name: 'WhatsApp (WABA)',
    displayName: 'WhatsApp WABA',
    icon: '📱',
    iconUrl: '/channels/whatsapp.png',
    color: '#25D366',
    requiresBinding: false, // WhatsApp доступен по номеру телефона
  },
  whatsapp_regular: {
    id: 'whatsapp_regular',
    name: 'WhatsApp (Regular)',
    displayName: 'WhatsApp Regular',
    icon: '📱',
    iconUrl: '/channels/whatsapp.png',
    color: '#25D366',
    requiresBinding: false, // Можно писать первым через Wazzup API
  },
  telegram: {
    id: 'telegram',
    name: 'Telegram',
    icon: '✈️',
    iconUrl: '/channels/telegram.png',
    color: '#0088cc',
    requiresBinding: true, // Требует привязки Telegram
  },
  telegram_bot: {
    id: 'telegram_bot',
    name: 'Telegram Bot',
    displayName: 'Telegram Bot',
    icon: '🤖',
    iconUrl: '/channels/telegram.png',
    color: '#0088cc',
    requiresBinding: true, // Пациент должен написать в бота и быть привязан
  },
  telegram_client: {
    id: 'telegram_client',
    name: 'Telegram Client',
    displayName: 'Telegram Client',
    icon: '📞',
    iconUrl: '/channels/telegram.png',
    color: '#0088cc',
    requiresBinding: true, // Номер должен быть открыт для поиска в TG (без приватности)
  },
  max: {
    id: 'max',
    name: 'MAX (VK)',
    displayName: 'MAX Messenger',
    icon: '💬',
    iconUrl: '/channels/max.webp',
    color: '#5181b8',
    requiresBinding: true, // MAX Bot — требует привязки
  },
  max_client: {
    id: 'max_client',
    name: 'MAX Client',
    displayName: 'MAX Client',
    icon: '💜',
    iconUrl: '/channels/max.webp',
    color: '#9333ea',
    requiresBinding: true, // MAX Client — клиентский аккаунт клиники
  },
  sms: {
    id: 'sms',
    name: 'SMS',
    icon: '📝',
    iconUrl: '/channels/sms.png',
    color: '#666',
    requiresBinding: false, // SMS по номеру телефона
  },
  sms_tel: {
    id: 'sms_tel',
    name: 'SMS-Tel',
    displayName: 'SMS-Tel',
    icon: '📲',
    iconUrl: '/channels/sms.png',
    color: '#475569',
    requiresBinding: false, // Android-телефон клиники через sms_bridge
  },
} as const;

export const DELIVERY_CHANNEL_IDS = [
  'whatsapp_waba',
  'whatsapp_regular',
  'telegram_bot',
  'telegram_client',
  'max',
  'sms',
  'sms_tel',
] as const;

/**
 * Получить конфигурацию канала по ID
 */
export function getChannelConfig(channelId: string): ChannelConfig | undefined {
  const normalized = normalizeChannelId(channelId);
  return CHANNELS[normalized as ChannelId] || CHANNELS[channelId as ChannelId];
}

/**
 * Получить список доступных каналов
 */
export function getAvailableChannels(availableIds: string[]): ChannelConfig[] {
  return availableIds
    .map(id => getChannelConfig(id))
    .filter((config): config is ChannelConfig => config !== undefined);
}

export function normalizeChannelId(channelId?: string): string {
  const channel = String(channelId || '').trim().toLowerCase();
  switch (channel) {
    case 'telegram':
    case 'telegram_bot':
      return 'telegram_bot';
    case 'whatsapp':
    case 'regular':
    case 'whatsapp_regular':
    case 'wazzup_regular':
      return 'whatsapp_regular';
    case 'whatsapp_waba':
    case 'wazzup_waba':
      return 'whatsapp_waba';
    case 'sms_tel':
    case 'sms-tel':
    case 'smstel':
    case 'android_sms':
      return 'sms_tel';
    default:
      return channel;
  }
}

export function normalizeChannelList(channelIds?: string[]): string[] {
  const normalized = (channelIds || []).map(normalizeChannelId).filter(Boolean);
  return Array.from(new Set(normalized));
}

export function normalizeTemplateMessenger(raw?: string): string {
  const messenger = String(raw || '').trim().toLowerCase();
  switch (messenger) {
    case 'telegram':
    case 'telegram_bot':
      return 'telegram_bot';
    case 'telegram_client':
      return 'telegram_client';
    case 'max_client':
      return 'max_client';
    case 'whatsapp':
    case 'regular':
    case 'whatsapp_regular':
    case 'wazzup_regular':
      return 'whatsapp_regular';
    case 'whatsapp_waba':
    case 'wazzup_waba':
      return 'whatsapp_waba';
    case 'sms_tel':
    case 'sms-tel':
    case 'smstel':
    case 'android_sms':
      return 'sms_tel';
    default:
      return messenger;
  }
}

interface TemplateLike {
  messenger?: string;
  template_type?: string;
  buttons?: Array<unknown>;
}

export function templateMatchesChannel(template: TemplateLike, channelId: string): boolean {
  const channel = normalizeChannelId(channelId);
  const messenger = normalizeTemplateMessenger(template.messenger);
  const isWabaTemplate = template.template_type === 'waba_template';
  if (!isWabaTemplate && messenger === 'universal') {
    return true;
  }

  switch (channel) {
    case 'whatsapp_waba':
      return isWabaTemplate;
    case 'whatsapp_regular':
      return !isWabaTemplate && messenger === 'whatsapp_regular';
    case 'telegram_bot':
      return !isWabaTemplate && messenger === 'telegram_bot';
    case 'telegram_client':
      return !isWabaTemplate && (messenger === 'telegram_bot' || messenger === 'telegram_client');
    case 'max':
      return !isWabaTemplate && messenger === 'max';
    case 'max_client':
      return !isWabaTemplate && messenger === 'max_client';
    case 'sms':
      return !isWabaTemplate && messenger === 'sms' && (!template.buttons || template.buttons.length === 0);
    case 'sms_tel':
      return !isWabaTemplate && (messenger === 'sms' || messenger === 'sms_tel') && (!template.buttons || template.buttons.length === 0);
    default:
      return true;
  }
}

export function filterTemplatesByChannel<T extends TemplateLike>(templates: T[], channelId: string): T[] {
  if (!channelId) {
    return templates;
  }
  return templates.filter((template) => templateMatchesChannel(template, channelId));
}

export function getChannelDisplayName(channelId?: string): string {
  const normalized = normalizeChannelId(channelId);
  if (normalized === 'all') return 'Все';
  if (normalized === 'none') return 'Нет канала';
  if (normalized === 'max_client') return 'MAX Client';
  const config = getChannelConfig(normalized);
  return config?.displayName || config?.name || String(channelId || '');
}

export function getChannelShortLabel(channelId?: string): string {
  const normalized = normalizeChannelId(channelId);
  switch (normalized) {
    case 'telegram_bot':
      return 'TG Bot';
    case 'telegram_client':
      return 'TG Cli';
    case 'max':
      return 'MAX';
    case 'max_client':
      return 'Max Cli';
    case 'whatsapp_waba':
      return 'WABA';
    case 'whatsapp_regular':
      return 'Reg';
    case 'sms':
      return 'SMS';
    case 'sms_tel':
      return 'SMS-Tel';
    default:
      return getChannelDisplayName(channelId);
  }
}

export function getChannelIcon(channelId?: string): string {
  const normalized = normalizeChannelId(channelId);
  if (normalized === 'none') return '❌';
  return getChannelConfig(normalized)?.icon || '📡';
}

export function getChannelIconUrl(channelId?: string): string | undefined {
  const normalized = normalizeChannelId(channelId);
  return getChannelConfig(normalized)?.iconUrl;
}

export function getChannelLabel(channelId?: string): string {
  const icon = getChannelIcon(channelId);
  const name = getChannelDisplayName(channelId);
  return `${icon} ${name}`.trim();
}

